import { lazy } from 'react';
import { Navigate, Routes, type RouteObject } from 'react-router';

import RouteSuspense from '@/components/suspenses/route-suspense/route-suspense';

import { APP_ROUTE } from './app.route.constant';
import { renderRoutes } from '@/libs/react-router';
import AppSuspenseFallback from '@/components/suspenses/app-suspense-fallback/app-suspense-fallback';
import { PermissionRouteGuard } from '@/features/roles/utils/permission-route-guard';
import { PERMISSIONS } from '@/features/roles/constants/permissions.constant';

const {
  APP,
  AUTH,
  ANALYTICS,
  PRODUCTS,
  ROLES,
  USERS,
  SESSIONS,
  WITHDRAWS,
  UNIVERSITIES,
  QUESTION_BANKS,
  ASSESSMENT_FORMATS,
  CERTIFICATES,
  VOUCHERS,
  ORDERS,
  TRANSACTIONS,
  SETTINGS,
  NOTIFICATIONS,
  NOTIFICATION_DELIVERIES,
  HIGHLIGHT_CONTENT,
  ERROR,
  ROUTE_NOT_FOUND,
  PRODUCT_CLAIM,
  ADMINS,
} = APP_ROUTE;

// Layouts
const AuthLayout = lazy(
  () => import('@/components/layouts/auth-layout/auth-layout')
);
const AppLayout = lazy(
  () => import('@/components/layouts/app-layout/app-layout')
);

// Auth pages
const SigninPage = lazy(() => import('@/app/auths/signin-page/signin-page'));

// Analytics pages
const DashboardPage = lazy(
  () => import('@/app/analytics/dashboard-page/dashboard-page')
);

// Products pages
const ListProductPage = lazy(
  () => import('@/app/products/list-product-page/list-product-page')
);
const CreateDigitalProductPage = lazy(
  () =>
    import('@/app/products/create-digital-product-page/create-digital-product-page')
);
const CreateEventProductPage = lazy(
  () =>
    import('@/app/products/create-event-product-page/create-event-product-page')
);
const UpdateDigitalProductPage = lazy(
  () =>
    import('@/app/products/update-digital-product-page/update-digital-product-page')
);
const UpdateEventProductPage = lazy(
  () =>
    import('@/app/products/update-event-product-page/update-event-product-page')
);
const CreateTryoutProductPage = lazy(
  () =>
    import('@/app/products/create-tryout-product-page/create-tryout-product-page')
);
const UpdateTryoutProductPage = lazy(
  () =>
    import('@/app/products/update-tryout-product-page/update-tryout-product-page')
);
const CreateBundleProductPage = lazy(
  () =>
    import('@/app/products/create-bundle-product-page/create-bundle-product-page')
);
const UpdateBundleProductPage = lazy(
  () =>
    import('@/app/products/update-bundle-product-page/update-bundle-product-page')
);

// Roles pages
const ListRolesPage = lazy(
  () => import('@/app/roles/list-roles-page/list-roles-page')
);

// Users pages
const ListUserPage = lazy(
  () => import('@/app/users/list-user-page/list-user-page')
);

// Admins pages
const ListAdminPage = lazy(
  () => import('@/app/admins/list-admin-page/list-admin-page')
);

// Sessions pages
const ListSessionPage = lazy(
  () => import('@/app/sessions/list-session-page/list-session-page')
);
const ListNoShowClaimPage = lazy(
  () => import('@/app/sessions/list-no-show-claim-page/list-no-show-claim-page')
);
const ListSessionMentorPage = lazy(
  () =>
    import('@/app/sessions/list-session-mentor-page/list-session-mentor-page')
);

// Withdraws pages
const ListWithdrawPage = lazy(
  () => import('@/app/withdraws/list-withdraw-page/list-withdraw-page')
);

// Universities pages
const ListUniversityPage = lazy(
  () => import('@/app/universities/list-university-page/list-university-page')
);

// Question Banks pages
const ListQuestionBankPage = lazy(
  () =>
    import('@/app/question-banks/list-question-bank-page/list-question-bank-page')
);
const CreateQuestionBankPage = lazy(
  () =>
    import('@/app/question-banks/create-question-bank-page/create-question-bank-page')
);
const UpdateQuestionBankPage = lazy(
  () =>
    import('@/app/question-banks/update-question-bank-page/update-question-bank-page')
);

// Assessment Formats pages
const ListAssessmentFormatPage = lazy(
  () =>
    import('@/app/assessment-formats/list-assessment-format-page/list-assessment-format-page')
);

// Certificates pages
const ListCertificatePage = lazy(
  () => import('@/app/certificates/list-certificate-page/list-certificate-page')
);

// Orders pages
const ListOrderPage = lazy(
  () => import('@/app/orders/list-order-page/list-order-page')
);

// Vouchers pages
const ListVoucherPage = lazy(
  () => import('@/app/vouchers/list-voucher-page/list-voucher-page')
);

// Klaim Produk pages
const ListProductClaimPage = lazy(
  () => import('@/app/product-claim/product-claim-list/list-product-claim-page')
);

// Transactions pages
const ListTransactionPage = lazy(
  () => import('@/app/transactions/list-transaction-page/list-transaction-page')
);

// Settings pages
const SettingsPage = lazy(
  () => import('@/app/settings/settings-page/settings-page')
);
const TestingComponentPage = lazy(
  () => import('@/app/settings/testing-component-page/testing-component-page')
);
const QrGeneratorPage = lazy(
  () => import('@/app/settings/qr-generator-page/qr-generator-page')
);

// Highlight Content pages
const HighlightContentPage = lazy(
  () =>
    import('@/app/highlight-content/highlight-content-page/highlight-content-page')
);

// Notifications pages
const NotificationsPage = lazy(
  () => import('@/app/notifications/notifications-page/notifications-page')
);

// Notification Deliveries pages
const ListNotificationDeliveryPage = lazy(
  () =>
    import('@/app/notification-deliveries/list-notification-delivery-page/list-notification-delivery-page')
);

// Error pages
const NotFoundPage = lazy(
  () => import('@/app/errors/not-found-page/not-found-page')
);
const ForbiddenPage = lazy(
  () => import('@/app/errors/forbidden-page/forbidden-page')
);
const MaintenancePage = lazy(
  () => import('@/app/errors/maintenance-page/maintenance-page')
);

export const routes: RouteObject[] = [
  {
    path: AUTH.ROOT.path,
    element: <RouteSuspense component={AuthLayout} />,
    children: [
      {
        index: true,
        element: <Navigate to={AUTH.SIGN_IN.getHref()} />,
      },
      {
        path: AUTH.SIGN_IN.path,
        element: <RouteSuspense component={SigninPage} />,
      },
    ],
  },

  {
    path: APP.ROOT.path,
    element: (
      <RouteSuspense
        component={AppLayout}
        suspenseFallback={<AppSuspenseFallback />}
      />
    ),
    children: [
      {
        index: true,
        element: <Navigate to={ANALYTICS.DASHBOARD.getHref()} />,
      },
      {
        path: ANALYTICS.DASHBOARD.path,
        element: <RouteSuspense component={DashboardPage} />,
      },
      {
        path: PRODUCTS.ROOT.path,
        children: [
          {
            index: true,
            element: <RouteSuspense component={ListProductPage} />,
          },
          {
            path: PRODUCTS.CREATE_DIGITAL.path,
            element: <RouteSuspense component={CreateDigitalProductPage} />,
          },
          {
            path: PRODUCTS.CREATE_EVENT.path,
            element: <RouteSuspense component={CreateEventProductPage} />,
          },
          {
            path: PRODUCTS.UPDATE_DIGITAL.path,
            element: <RouteSuspense component={UpdateDigitalProductPage} />,
          },
          {
            path: PRODUCTS.UPDATE_EVENT.path,
            element: <RouteSuspense component={UpdateEventProductPage} />,
          },
          {
            path: PRODUCTS.CREATE_TRYOUT.path,
            element: <RouteSuspense component={CreateTryoutProductPage} />,
          },
          {
            path: PRODUCTS.UPDATE_TRYOUT.path,
            element: <RouteSuspense component={UpdateTryoutProductPage} />,
          },
          {
            path: PRODUCTS.CREATE_BUNDLE.path,
            element: <RouteSuspense component={CreateBundleProductPage} />,
          },
          {
            path: PRODUCTS.UPDATE_BUNDLE.path,
            element: <RouteSuspense component={UpdateBundleProductPage} />,
          },
        ],
      },
      {
        path: ROLES.ROOT.path,
        element: <RouteSuspense component={ListRolesPage} />,
      },
      {
        path: USERS.ROOT.path,
        element: <RouteSuspense component={ListUserPage} />,
      },
      {
        path: ADMINS.ROOT.path,
        element: <RouteSuspense component={ListAdminPage} />,
      },
      {
        path: SESSIONS.ROOT.path,
        children: [
          {
            index: true,
            element: <RouteSuspense component={ListSessionPage} />,
          },
          {
            path: SESSIONS.MENTOR.path,
            element: <RouteSuspense component={ListSessionMentorPage} />,
          },
          {
            path: SESSIONS.NO_SHOW_CLAIMS.path,
            element: <RouteSuspense component={ListNoShowClaimPage} />,
          },
        ],
      },
      {
        path: WITHDRAWS.ROOT.path,
        element: <RouteSuspense component={ListWithdrawPage} />,
      },
      {
        path: UNIVERSITIES.ROOT.path,
        element: <RouteSuspense component={ListUniversityPage} />,
      },
      {
        path: QUESTION_BANKS.ROOT.path,
        children: [
          {
            index: true,
            element: <RouteSuspense component={ListQuestionBankPage} />,
          },
          {
            path: QUESTION_BANKS.CREATE.path,
            element: <RouteSuspense component={CreateQuestionBankPage} />,
          },
          {
            path: QUESTION_BANKS.UPDATE.path,
            element: <RouteSuspense component={UpdateQuestionBankPage} />,
          },
        ],
      },
      {
        path: ASSESSMENT_FORMATS.ROOT.path,
        element: <RouteSuspense component={ListAssessmentFormatPage} />,
      },
      {
        path: CERTIFICATES.ROOT.path,
        element: <RouteSuspense component={ListCertificatePage} />,
      },
      {
        path: ORDERS.ROOT.path,
        element: <RouteSuspense component={ListOrderPage} />,
      },
      {
        path: VOUCHERS.ROOT.path,
        element: <RouteSuspense component={ListVoucherPage} />,
      },
      {
        path: PRODUCT_CLAIM.ROOT.path,
        element: <RouteSuspense component={ListProductClaimPage} />,
      },
      {
        path: TRANSACTIONS.ROOT.path,
        element: <RouteSuspense component={ListTransactionPage} />,
      },
      {
        path: SETTINGS.ROOT.path,
        children: [
          {
            index: true,
            element: <RouteSuspense component={SettingsPage} />,
          },
          {
            path: SETTINGS.TESTING_COMPONENT.path,
            element: <RouteSuspense component={TestingComponentPage} />,
          },
          {
            path: SETTINGS.QR_GENERATOR.path,
            element: <RouteSuspense component={QrGeneratorPage} />,
          },
        ],
      },
      {
        path: HIGHLIGHT_CONTENT.ROOT.path,
        element: <RouteSuspense component={HighlightContentPage} />,
      },
      {
        path: NOTIFICATIONS.ROOT.path,
        element: <RouteSuspense component={NotificationsPage} />,
      },
      {
        path: NOTIFICATION_DELIVERIES.ROOT.path,
        element: (
          <PermissionRouteGuard
            permission={PERMISSIONS.NOTIFICATION_DELIVERY_VIEW}
          >
            <RouteSuspense component={ListNotificationDeliveryPage} />
          </PermissionRouteGuard>
        ),
      },
    ],
  },

  {
    path: ERROR.NOT_FOUND.path,
    element: <RouteSuspense component={NotFoundPage} />,
  },
  {
    path: ERROR.FORBIDDEN.path,
    element: <RouteSuspense component={ForbiddenPage} />,
  },
  {
    path: ERROR.MAINTENANCE.path,
    element: <RouteSuspense component={MaintenancePage} />,
  },

  {
    path: ROUTE_NOT_FOUND.path,
    element: <Navigate to={ERROR.NOT_FOUND.getHref()} />,
  },
];

// APP ROUTE COMPONENT
export default function AppRoute() {
  return <Routes>{renderRoutes(routes)}</Routes>;
}
